import { ApiError } from '@lib/network/api';
import { formatBytesToString, formatDurationToString } from '@lib/utils/units';
import { formatDistanceStrict } from 'date-fns';
import { getSocialPlatformModule, type SocialPostContent } from './platforms';
import type { PreflightViolation } from './preflight';
import type { SocialPostStatus } from './status';
import type { PostContentSummary } from './types';

// ── Status ─────────────────────────────────────────────────────
//
// The vocabulary and the action table live in `status.ts`; this file words it.

export type SocialPostStatusTone = 'neutral' | 'active' | 'success' | 'error' | 'warning';

export type SocialPostStatusPresentation = { label: string; tone: SocialPostStatusTone };

/** Copy and tone per status. `needs_approval` reads like `scheduled`: nothing produces it in v1. */
const SOCIAL_POST_STATUS_PRESENTATION: Record<SocialPostStatus, SocialPostStatusPresentation> = {
  needs_approval: { label: 'Scheduled', tone: 'neutral' },
  scheduled: { label: 'Scheduled', tone: 'neutral' },
  processing: { label: 'Publishing', tone: 'active' },
  published: { label: 'Published', tone: 'success' },
  failed: { label: 'Failed', tone: 'error' },
  needs_attention: { label: 'Needs attention', tone: 'warning' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

/** A status newer than this client degrades to its raw name in neutral, like the sibling vocabularies below. */
export const getStatusPresentation = (status: string): SocialPostStatusPresentation =>
  Object.prototype.hasOwnProperty.call(SOCIAL_POST_STATUS_PRESENTATION, status)
    ? SOCIAL_POST_STATUS_PRESENTATION[status as SocialPostStatus]
    : { label: status, tone: 'neutral' };

// ── Refusal codes ──────────────────────────────────────────────
//
// One vocabulary in three places: a violation on a 422, the last attempt's
// code on a stopped post, and the verdict on `available: false` capabilities.

const REFUSAL_CODE_COPY: Record<string, string> = {
  connection_not_found: 'That account is no longer connected.',
  connection_needs_reauth: 'Reconnect the account first.',
  platform_unavailable: 'This platform is not available here.',
  content_platform_mismatch: 'The post content does not match the account’s platform.',
  content_invalid: 'A field is not valid for this platform.',
  duration_too_short: 'The media is shorter than this account may post.',
  duration_too_long: 'The media is longer than this account may post.',
  file_too_large: 'The file is over the platform’s size limit.',
  resolution_too_low: 'The frame is too small for a reel.',
  aspect_ratio_unsupported:
    'The frame’s shape is not one a reel allows. Post it as a video instead.',
  privacy_not_available: 'The account cannot post at that privacy level.',
  posting_limit_reached: 'The account hit its daily posting cap. Try again tomorrow.',
  spam_rejected: 'The platform’s spam controls refused the post.',
  app_limitation: 'A limit on our side, not on your account. We are on it.',
  media_rejected: 'The platform refused the file after its own checks.',
};

/** Null for a code newer than this client: the row then shows the status alone. */
export const describeRefusalCode = (code: string): string | null =>
  Object.prototype.hasOwnProperty.call(REFUSAL_CODE_COPY, code) ? REFUSAL_CODE_COPY[code] : null;

/** `pixels` is a side of the frame (`resolution_too_low`); the API says which side in its message, the copy says "frame". */
const UNIT_FORMAT: Record<NonNullable<PreflightViolation['unit']>, (value: number) => string> = {
  seconds: formatDurationToString,
  bytes: formatBytesToString,
  pixels: (value) => `${value.toLocaleString()} px`,
};

/** A preflight violation as one line under the refused account: the worded code, with the bound when the API gave one. */
export const describeViolation = (violation: PreflightViolation): string => {
  const base = describeRefusalCode(violation.code) ?? `Refused (${violation.code}).`;
  if (violation.limit === null || violation.actual === null || !violation.unit) return base;
  const format = UNIT_FORMAT[violation.unit];
  return `${base} Limit ${format(violation.limit)}, this media is ${format(violation.actual)}.`;
};

/**
 * Copy for an `ApiError` from any `/social/*` or render call the scheduling
 * pages make, by the API's code (or status when the code is generic). Null
 * for an error this client has no words for; the caller adds its own
 * fallback, which names the action that failed.
 */
export const describeSocialApiError = (error: unknown): string | null => {
  if (!(error instanceof ApiError)) return null;
  switch (error.code) {
    case 'social.connection.needs_reauth':
      return 'The account needs to be reconnected first.';
    case 'social.connection.not_found':
      return 'The account is no longer connected.';
    case 'social.platform.unreachable':
      return 'The platform could not be reached. Try again shortly.';
    case 'rendering.notFound':
      return 'The render could not be found.';
    case 'rendering.forbidden':
      return 'This render belongs to another account.';
    case 'social.post.rendering_not_publishable':
      return 'The render failed, so there is no media to publish.';
  }
  if (error.status === 429) return 'Too many requests. Wait a minute and try again.';
  if (error.status === 503) return 'The platform could not be reached. Try again shortly.';
  return null;
};

/** `describeSocialApiError` with the generic fallback for a failed action. */
export const describeScheduleError = (error: unknown): string =>
  describeSocialApiError(error) ?? 'Something went wrong. Try again later.';

export type SocialPostAttentionReason =
  | 'connection_needs_reauth'
  | 'connection_missing'
  | 'attempt_unconfirmed';

const ATTENTION_REASON_COPY: Record<SocialPostAttentionReason, string> = {
  connection_needs_reauth: 'Reconnect the account, then retry.',
  connection_missing: 'The account was disconnected.',
  attempt_unconfirmed:
    'The platform may already have this post. Check the account before retrying: a retry can post twice.',
};

export const describeAttentionReason = (reason: string): string | null =>
  Object.prototype.hasOwnProperty.call(ATTENTION_REASON_COPY, reason)
    ? ATTENTION_REASON_COPY[reason as SocialPostAttentionReason]
    : null;

export type SocialPostPhaseName =
  | 'notifying_platform'
  | 'pushing_media'
  | 'awaiting_media_fetch'
  | 'platform_processing';

const PHASE_COPY: Record<SocialPostPhaseName, string> = {
  notifying_platform: 'Contacting the platform',
  pushing_media: 'Uploading the media',
  awaiting_media_fetch: 'The platform is fetching the media',
  platform_processing: 'The platform is processing the media',
};

export const describePhase = (name: string): string =>
  Object.prototype.hasOwnProperty.call(PHASE_COPY, name)
    ? PHASE_COPY[name as SocialPostPhaseName]
    : 'Publishing';

/**
 * The second line of a stopped or in-flight post, if any: the phase and how
 * long it has been there, the attention reason, or the last refusal. `now`
 * is the caller's clock so a ticking elapsed label re-renders on its terms.
 */
export const describePostDetail = (post: SocialPost, now: number): string | null => {
  switch (post.status) {
    case 'processing':
      return post.phase
        ? `${describePhase(post.phase.name)} · ${formatDistanceStrict(post.phase.since, now)}`
        : describePhase('');
    case 'needs_attention':
      return post.attentionReason ? describeAttentionReason(post.attentionReason) : null;
    case 'failed':
      return post.lastAttemptCode ? describeRefusalCode(post.lastAttemptCode) : null;
    default:
      return null;
  }
};

// ── Wire → owned ───────────────────────────────────────────────

/** `GET /social/schedule/posts` row as the API sends it. Fields drift by lifecycle stage, hence the optionals. */
export type SocialPostWire = {
  _id: string;
  connectionId: string;
  platform: string;
  groupId?: string;
  renderId?: string;
  publishAt: number;
  status: string;
  content?: unknown;
  publishing?: {
    attempts?: { at: number; kind?: string; code?: string; message?: string }[];
    result?: { externalPostId?: string | null; permalink?: string | null };
    phase?: { name: string; since: number };
    attentionReason?: string;
  };
  createdAt?: number;
  updatedAt?: number;
};

export type SocialPost = {
  id: string;
  connectionId: string;
  /** The API's platform id. Unknown values come from connectors newer than this client. */
  platform: string;
  /** Shared by the posts of one schedule call. */
  groupId: string | null;
  renderId: string | null;
  /** Unix ms. */
  publishAt: number;
  /** One of `SocialPostStatus`, or a value newer than this client; read it through `getStatusPresentation`. */
  status: string;
  /** Platform-exact content, kept whole for edit. Null when this client does not know the platform. */
  content: SocialPostContent | null;
  /** The module's reading of `content`; null with it, for a platform this client does not know. */
  summary: PostContentSummary | null;
  /** The last failed attempt's code, worded by `describeRefusalCode`. */
  lastAttemptCode: string | null;
  attentionReason: string | null;
  /** Set while `processing` (and kept on an unconfirmed attempt as evidence). */
  phase: { name: string; since: number } | null;
  /** Only once the platform issued it; never for a draft. */
  permalink: string | null;
  /** Unix ms. */
  updatedAt: number;
};

export const toSocialPost = (wire: SocialPostWire): SocialPost => {
  if (!wire._id) throw new Error('social post without _id');
  if (typeof wire.publishAt !== 'number')
    throw new Error(`social post ${wire._id} without publishAt`);
  const module = getSocialPlatformModule(wire.platform);
  // Every stored post carries its content (the schedule call requires it per
  // target), so a known platform without one is a backend bug, like a shape
  // that is not the platform's; only an unknown platform reads as null.
  if (module && !wire.content) throw new Error(`social post ${wire._id} without content`);
  const content = module ? module.parseContent(wire.content) : null;
  const attempts = wire.publishing?.attempts ?? [];
  return {
    id: wire._id,
    connectionId: wire.connectionId,
    platform: wire.platform,
    groupId: wire.groupId || null,
    renderId: wire.renderId || null,
    publishAt: wire.publishAt,
    status: wire.status,
    content,
    summary: module && content ? module.describeContent(content) : null,
    lastAttemptCode: attempts[attempts.length - 1]?.code || null,
    attentionReason: wire.publishing?.attentionReason || null,
    phase: wire.publishing?.phase ?? null,
    permalink: wire.publishing?.result?.permalink || null,
    updatedAt: wire.updatedAt ?? wire.publishAt,
  };
};
